import { FIELD_TECHS_CRUD_ACTION_CREATORS } from '../actions/field_techs_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { FieldTechHomeAddress } from '../utils/WrenchModeEventUtils';
const fetchUtils = new FetchUtils();
const baseUrl = getBackendUri();

export const fieldTechsRestCrudThunksForBuyer = new RestCrudThunks(
	'field_techs',
	'field_tech',
	'fieldTech',
	'fieldTechs',
	FIELD_TECHS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'field_tech/'
);

export const fieldTechsRestCrudThunksForSupplier = new RestCrudThunks(
	'field_techs',
	'field_tech',
	'fieldTech',
	'fieldTechs',
	FIELD_TECHS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'field_tech/'
);

// This api does not support no_pagination, confirmed with palani. Can be supported and be improved in V2
const FIELD_TECHS_PAGE_SIZE = 50;

export const fetchAllFieldTechs =
	(roleType) =>
	(targetCollectionName, sorting: any = { sort_by: 'name', order: 'ascend' }) =>
	async (dispatch, getState) => {
		const fieldTechsThunks =
			roleType === ROLE_TYPES.SUPPLIER
				? fieldTechsRestCrudThunksForSupplier
				: fieldTechsRestCrudThunksForBuyer;

		// Pages after the first append, so the target collection ends up holding every tech.
		const readPage = (page, addToTargetCollection) =>
			dispatch(
				fieldTechsThunks.read(
					{},
					targetCollectionName,
					{ current: page, pageSize: FIELD_TECHS_PAGE_SIZE },
					sorting,
					null,
					addToTargetCollection
				)
			);

		const fieldTechs = [...((await readPage(1, false)) || [])];
		const count = nullSafeGetOrElse('count', getState().field_techs[targetCollectionName], 0);
		const pageCount = Math.ceil(count / FIELD_TECHS_PAGE_SIZE);

		// Sequential: `read` cancels an in-flight request for the same target collection.
		for (let page = 2; page <= pageCount; page++) {
			fieldTechs.push(...((await readPage(page, true)) || []));
		}

		return fieldTechs;
	};

export const allowFieldTechToManageStockLocations = (roleType) => (entity) => {
	const apiURL = `/api/v1/${roleType}/field_tech/field_techs/allow_field_tech_to_manage_stock_location/${entity.email}`;
	const baseUrl = getBackendUri();
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateStart(entity));
			fetchUtils
				.put(baseUrl + apiURL, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: entity,
				})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (d.status === 'error') {
						dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
						reject('An error occurred in updating field tech.' || d.message);
					} else {
						dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
					reject('An error occurred in updating field tech.' || d.message);
				});
		});
	};
};

export const allowFieldTechToManageStockLocationsForSupplier = allowFieldTechToManageStockLocations(
	ROLE_TYPES.SUPPLIER
);

// Partial update of a single field tech. The backend PATCH only reads the fields present in the
// payload (allProblemTypes, problemTypeIds, inventorySupervisorEmails) and preserves everything
// else, so each edit card can send just the attribute it owns without clobbering the others.
//
// `allProblemTypes` is the only way to express the problem-type tri-state, because a JSON null is
// indistinguishable from an omitted key by the time the server flattens the body:
//   allProblemTypes: true                    -> stored as NULL, i.e. every problem type
//   allProblemTypes: false + problemTypeIds  -> stored verbatim; [] means none are assigned
//   allProblemTypes omitted                  -> legacy merge; an absent list keeps what is stored
export const patchFieldTech =
	(roleType) =>
	(payload: {
		email: string;
		allProblemTypes?: boolean;
		problemTypeIds?: number[];
		inventorySupervisorEmails?: string[];
	}) => {
		const apiURL = `/api/v1/${roleType}/field_tech/field_techs`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateStart(payload));
				fetchUtils
					.patch(baseUrl + apiURL, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: payload,
					})
					.then(parseJSON)
					.then(({ json }) => {
						const d = json;
						if (d.status === 'error') {
							dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, payload));
							reject(d.message || 'An error occurred in updating field tech.');
						} else {
							dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, payload));
						reject(d.message || 'An error occurred in updating field tech.');
					});
			});
		};
	};

export const patchFieldTechForSupplier = patchFieldTech(ROLE_TYPES.SUPPLIER);

/**
 * Partial update of the CONTACT-level details of a field tech — their name and their EPA 608
 * certification. Separate from the field-tech PUT because these are columns on the contact row rather
 * than on the field tech: `RefrigerantEventServiceImpl` resolves a refrigerant event's
 * `technicianEmail` to a contact and reads the credential there, so a certification stored anywhere
 * else could not satisfy the 608 write rule.
 *
 * Every field is optional and an omitted key preserves the stored value, so each caller sends only
 * what it owns. The certification pair additionally distinguishes an omitted key from an explicit
 * `null`: null CLEARS the credential (a certification can lapse), absent leaves it alone. antd hands
 * back `undefined` for a cleared Select and `JSON.stringify` drops undefined keys, so a caller that
 * wants to clear has to send a real null — see `FieldTechForm.handleSubmit`.
 *
 * The backend keys off `userId` (the target field tech's email). The route is
 * `updateFieldTechDetails`; the server still answers on the old `updateFieldTechName` path with the
 * same action, so an older client keeps working.
 */
export const updateFieldTechDetails =
	(roleType) =>
	(payload: {
		userId: string;
		nameGiven?: string;
		nameFamily?: string;
		epaCertificationType?: string | null;
		epaCertificationNumber?: string | null;
	}) => {
		const apiURL = `/api/v1/${roleType}/updateFieldTechDetails`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateStart(payload));
				fetchUtils
					.patch(baseUrl + apiURL, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: payload,
					})
					.then(parseJSON)
					.then(({ json }) => {
						const d = json;
						if (d.status === 'error') {
							dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, payload));
							reject(d.message || 'An error occurred in updating field tech details.');
						} else {
							dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(FIELD_TECHS_CRUD_ACTION_CREATORS.updateError(d.message, payload));
						reject(d.message || 'An error occurred in updating field tech details.');
					});
			});
		};
	};

export const updateFieldTechDetailsForSupplier = updateFieldTechDetails(ROLE_TYPES.SUPPLIER);

export interface FieldTechHomeAddressPayload {
	id?: number;
	fieldTechEmail: string;
	name: string;
	address: FieldTechHomeAddress['address'];
	latitude: number;
	longitude: number;
}

const fieldTechHomeAddressesUrl = (roleType) =>
	`${baseUrl}/api/v1/${roleType}/field_tech/home_addresses`;

// No CRUD actions: the list is local to the field tech's Home Addresses card, which owns the refresh.
const resolveHomeAddressResponse = (request: Promise<any>, fallbackMessage: string) =>
	new Promise((resolve, reject) => {
		request
			.then(parseJSON)
			.then(({ ok, json }) => {
				if (ok && nullSafeGetOrElse('status', json, '') !== 'error') {
					resolve(nullSafeGet('data', json));
				} else {
					reject(nullSafeGetOrElse('message', json, fallbackMessage));
				}
			})
			.catch(() => reject(fallbackMessage));
	});

export const fetchFieldTechHomeAddresses = (roleType) => (fieldTechEmail: string) => () =>
	resolveHomeAddressResponse(
		fetchUtils.get(fieldTechHomeAddressesUrl(roleType), { fieldTechEmail }),
		'Unable to load home addresses.'
	);

export const createFieldTechHomeAddress =
	(roleType) => (homeAddress: FieldTechHomeAddressPayload) => () =>
		resolveHomeAddressResponse(
			fetchUtils.post(fieldTechHomeAddressesUrl(roleType), { data: homeAddress }),
			'Unable to add home address.'
		);

export const updateFieldTechHomeAddress =
	(roleType) => (homeAddress: FieldTechHomeAddressPayload) => () =>
		resolveHomeAddressResponse(
			fetchUtils.put(fieldTechHomeAddressesUrl(roleType), { data: homeAddress }),
			'Unable to update home address.'
		);

export const deleteFieldTechHomeAddress = (roleType) => (homeAddressId: number) => () =>
	resolveHomeAddressResponse(
		fetchUtils.delete(`${fieldTechHomeAddressesUrl(roleType)}/${homeAddressId}`),
		'Unable to delete home address.'
	);
